import { useState, useEffect, useCallback } from "react";
import { useFetch } from "@/hooks/useFetch";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { PeriodSelector } from "@/components/PeriodSelector";
import { num, brl, km, fmtDate, hms, inRange } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Minus, Pencil, CheckCircle2, XCircle, History, Check, X, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function DriverHistoryDialog({ driver, start, end, onClose, onSaved }) {
  const { data, loading, error, refetch, setData } = useFetch(`/admin/drivers/${driver.id}/history`);
  const [editDate, setEditDate] = useState(null);
  const [countVal, setCountVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ date: today(), count: "" });
  const [manualAdded, setManualAdded] = useState([]);
  const [deletedDates, setDeletedDates] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`delHist_${driver.id}`) || "[]"); } catch { return []; }
  });
  const [confirmDate, setConfirmDate] = useState(null);

  const all = data?.history || [];
  const isEmpty = (h) => (Number(h.count) || 0) <= 0 && (Number(h.routes_count) || 0) <= 0 && (Number(h.worked_seconds) || 0) <= 0;
  const rows = all.filter((h) => !deletedDates.includes(h.date) && !isEmpty(h) && (inRange(h.date, start, end) || manualAdded.includes(h.date)));

  const startEdit = (h) => { setConfirmDate(null); setEditDate(h.date); setCountVal(String(h.count ?? "")); };

  const deleteRecord = async (h) => {
    setBusy(true);
    try {
      await api.post("/admin/deliveries/history", { driver_id: driver.id, date: h.date, count: 0 });
      toast.success("Registro excluído");
      setDeletedDates((s) => {
        const next = Array.from(new Set([...s, h.date]));
        try { localStorage.setItem(`delHist_${driver.id}`, JSON.stringify(next)); } catch (_) {}
        return next;
      });
      setData((prev) => ({ ...(prev || {}), history: ((prev && prev.history) || []).filter((x) => x.date !== h.date) }));
      setConfirmDate(null);
      await refetch();
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addRecord = async () => {
    const dateStr = addForm.date;
    const c = parseInt(addForm.count, 10);
    if (!dateStr || isNaN(c) || c < 0) { toast.error("Informe data e quantidade válidas"); return; }
    setBusy(true);
    try {
      await api.post("/admin/deliveries/history", { driver_id: driver.id, date: dateStr, count: c });
      toast.success("Registro adicionado manualmente");
      // Atualização otimista: garante que a linha apareça imediatamente
      setData((prev) => {
        const hist = [...((prev && prev.history) || [])];
        const idx = hist.findIndex((x) => x.date === dateStr);
        if (idx >= 0) hist[idx] = { ...hist[idx], count: c };
        else hist.push({ date: dateStr, count: c, finalized: true, routes_count: 0, worked_seconds: 0, km: 0, edited_by_name: "você (salvando…)" });
        return { ...(prev || {}), history: hist };
      });
      setManualAdded((s) => Array.from(new Set([...s, dateStr])));
      setAddOpen(false);
      setAddForm({ date: today(), count: "" });
      await refetch();
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const save = async (h) => {
    setBusy(true);
    try {
      await api.post("/admin/deliveries/history", { driver_id: driver.id, date: h.date, count: parseInt(countVal, 10) || 0 });
      toast.success("Registro atualizado");
      setEditDate(null);
      await refetch();
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl border-slate-800 bg-slate-900" data-testid="driver-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100"><History className="h-5 w-5 text-sky-400" /> Histórico de entregas — {driver.name}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {start && end ? `Período ${fmtDate(start)} a ${fmtDate(end)}` : "Todos os registros disponíveis"} · {rows.length} registro(s). Clique no lápis para corrigir a quantidade.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">Registros inseridos manualmente aparecem marcados com o autor.</p>
          <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10" onClick={() => setAddOpen((v) => !v)} data-testid="history-add-toggle">
            <Plus className="h-4 w-4" /> Adicionar registro
          </Button>
        </div>
        {addOpen && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-orange-500/30 bg-slate-950/50 p-3" data-testid="history-add-form">
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Data</Label>
              <Input type="date" value={addForm.date} onChange={(e) => setAddForm((s) => ({ ...s, date: e.target.value }))} data-testid="history-add-date" className="h-9 border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">Quantidade de entregas</Label>
              <Input type="number" min="0" value={addForm.count} onChange={(e) => setAddForm((s) => ({ ...s, count: e.target.value }))} data-testid="history-add-count" className="h-9 w-40 border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <Button className="h-9 bg-orange-500 text-white hover:bg-orange-600" disabled={busy} onClick={addRecord} data-testid="history-add-save">Salvar registro</Button>
            <Button variant="ghost" className="h-9 text-slate-400" onClick={() => setAddOpen(false)}>Cancelar</Button>
          </div>
        )}
        {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
          <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-slate-800">
            <table className="w-full text-sm" data-testid="driver-history-table">
              <thead className="sticky top-0 bg-slate-900">
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 font-semibold">Data</th>
                  <th className="px-3 py-2 font-semibold">Entregas</th>
                  <th className="px-3 py-2 font-semibold">KM</th>
                  <th className="px-3 py-2 font-semibold">Rotas</th>
                  <th className="px-3 py-2 font-semibold">Trabalhado</th>
                  <th className="px-3 py-2 font-semibold">Finalizado</th>
                  <th className="px-3 py-2 text-right font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.length === 0 && <tr><td colSpan={7}><Empty label="Sem registros no período" /></td></tr>}
                {rows.map((h) => (
                  <tr key={h.date} className="hover:bg-slate-800/40" data-testid={`history-row-${h.date}`}>
                    <td className="px-3 py-2 text-slate-300">{fmtDate(h.date)}</td>
                    <td className="px-3 py-2">
                      {editDate === h.date ? (
                        <Input type="number" min="0" value={countVal} onChange={(e) => setCountVal(e.target.value)} data-testid={`history-count-input-${h.date}`} className="h-8 w-24 border-slate-700 bg-slate-950 text-slate-100" />
                      ) : (
                        <span className="font-mono text-base font-bold text-orange-300">{num(h.count)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-400">{km(h.km)}</td>
                    <td className="px-3 py-2 font-mono text-slate-400">{num(h.routes_count)}</td>
                    <td className="px-3 py-2 text-slate-400">{hms(h.worked_seconds)}</td>
                    <td className="px-3 py-2">
                      {h.finalized ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Sim</span> : <span className="inline-flex items-center gap-1 text-xs text-slate-500"><XCircle className="h-3.5 w-3.5" /> Não</span>}
                      {h.edited_by_name && (
                        <span className="mt-1 flex w-fit items-center gap-1 rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-orange-300" data-testid={`history-manual-${h.date}`}>
                          <Pencil className="h-2.5 w-2.5" /> Manual · {h.edited_by_name}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1.5">
                        {confirmDate === h.date ? (
                          <>
                            <span className="self-center text-xs font-medium text-rose-300">Excluir esta data?</span>
                            <Button size="sm" className="h-8 bg-rose-600 px-2 text-white hover:bg-rose-700" disabled={busy} onClick={() => deleteRecord(h)} data-testid={`history-delete-confirm-${h.date}`}><Check className="h-4 w-4" /></Button>
                            <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" disabled={busy} onClick={() => setConfirmDate(null)} data-testid={`history-delete-cancel-${h.date}`}><X className="h-4 w-4" /></Button>
                          </>
                        ) : editDate === h.date ? (
                          <>
                            <Button size="sm" className="h-8 bg-emerald-600 px-2 text-white hover:bg-emerald-700" disabled={busy} onClick={() => save(h)} data-testid={`history-save-${h.date}`}><Check className="h-4 w-4" /></Button>
                            <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" disabled={busy} onClick={() => setEditDate(null)} data-testid={`history-cancel-${h.date}`}><X className="h-4 w-4" /></Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-orange-300 hover:text-orange-200" onClick={() => startEdit(h)} data-testid={`history-edit-${h.date}`}><Pencil className="h-4 w-4" /> Corrigir</Button>
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-400 hover:text-rose-300" onClick={() => { setEditDate(null); setConfirmDate(h.date); }} data-testid={`history-delete-${h.date}`} title="Excluir data"><Trash2 className="h-4 w-4" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AuditoriaEntregas() {
  const { data, loading, error, refetch } = useFetch("/admin/drivers");
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState(null);
  const [count, setCount] = useState("");
  const [hist, setHist] = useState(null);
  const [histForm, setHistForm] = useState({ date: today(), count: "" });
  const [historyDriver, setHistoryDriver] = useState(null);

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [period, setPeriod] = useState(null);
  const [pLoading, setPLoading] = useState(false);

  const loadPeriod = useCallback(() => {
    if (!start || !end) { setPeriod(null); return Promise.resolve(); }
    setPLoading(true);
    const y = Number(start.slice(0, 4));
    const m = Number(start.slice(5, 7));
    const q = Number(start.slice(8, 10)) <= 15 ? 1 : 2;
    return api.get("/admin/reports", { params: { year: y, month: m, quinzena: q, start, end } })
      .then((res) => setPeriod(res.data))
      .catch((e) => { toast.error(apiError(e)); setPeriod(null); })
      .finally(() => setPLoading(false));
  }, [start, end]);

  useEffect(() => { loadPeriod(); }, [loadPeriod]);

  const onHistorySaved = useCallback(() => { refetch(); loadPeriod(); }, [refetch, loadPeriod]);

  if (loading && !data) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const drivers = data?.drivers || [];

  const adjust = async (driver, body, key) => {
    setBusy(`${driver.id}-${key}`);
    try {
      await api.post("/admin/deliveries", { driver_id: driver.id, ...body });
      toast.success("Entregas atualizadas");
      await refetch();
      setEdit(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const saveHistory = async () => {
    setBusy("hist");
    try {
      await api.post("/admin/deliveries/history", { driver_id: hist.id, date: histForm.date, count: parseInt(histForm.count, 10) || 0 });
      toast.success("Histórico de entregas atualizado");
      await refetch();
      setHist(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="auditoria-entregas">
      <PeriodSelector start={start} end={end} setStart={setStart} setEnd={setEnd} testPrefix="entregas-period" clearable defaultActive={null} />

      {start && end ? (
        pLoading ? <Loading /> : (
          <>
            <p className="text-sm text-slate-400">Entregas por entregador no período <span className="text-orange-300">{period?.label}</span> (somente leitura). Limpe o período para ajustar.</p>
            <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
              <table className="w-full text-sm" data-testid="entregas-period-table">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Entregador</th>
                    <th className="px-4 py-3 font-semibold">Veículo</th>
                    <th className="px-4 py-3 font-semibold">Entregas</th>
                    <th className="px-4 py-3 font-semibold">KM</th>
                    <th className="px-4 py-3 font-semibold">Reembolso combustível</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(period?.rows || []).length === 0 && <tr><td colSpan={5}><Empty /></td></tr>}
                  {(period?.rows || []).map((r) => (
                    <tr key={r.id} className="hover:bg-slate-800/40">
                      <td className="px-4 py-3">
                        <button onClick={() => setHistoryDriver({ id: r.id, name: r.name })} data-testid={`entregas-name-${r.id}`} className="font-medium text-slate-100 underline-offset-2 hover:text-orange-300 hover:underline">{r.name}</button>
                      </td>
                      <td className="px-4 py-3 capitalize text-slate-400">{r.vehicle_type || "—"}</td>
                      <td className="px-4 py-3 font-mono text-lg font-bold text-orange-300">{num(r.deliveries)}</td>
                      <td className="px-4 py-3 font-mono text-slate-300">{km(r.km)}</td>
                      <td className="px-4 py-3 font-mono text-emerald-300">{brl(r.reimbursement)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : (
        <>
          <p className="text-sm text-slate-400">Ajuste a contagem de entregas do dia (rápido ou definir total) e corrija o histórico por data. Selecione um período para ver o total por intervalo.</p>
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
            <table className="w-full text-sm" data-testid="entregas-table">
              <thead>
                <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Entregador</th>
                  <th className="px-4 py-3 font-semibold">Veículo</th>
                  <th className="px-4 py-3 font-semibold">Hoje</th>
                  <th className="px-4 py-3 font-semibold">Finalizado</th>
                  <th className="px-4 py-3 font-semibold">Quinzena</th>
                  <th className="px-4 py-3 text-right font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {drivers.length === 0 && <tr><td colSpan={6}><Empty /></td></tr>}
                {drivers.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <button onClick={() => setHistoryDriver({ id: d.id, name: d.name })} data-testid={`entregas-name-${d.id}`} className="font-medium text-slate-100 underline-offset-2 hover:text-orange-300 hover:underline">{d.name}</button>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-400">{d.vehicle_type || "—"}</td>
                    <td className="px-4 py-3 font-mono text-lg font-bold text-orange-300">{num(d.deliveries_today)}</td>
                    <td className="px-4 py-3">
                      {d.finalized_today ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Sim</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500"><XCircle className="h-4 w-4" /> Não</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-200">{num(d.deliveries_fortnight)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 w-8 border-slate-700 p-0 text-slate-300" disabled={busy === `${d.id}-minus`} onClick={() => adjust(d, { delta: -1 }, "minus")} data-testid={`del-minus-${d.id}`}><Minus className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" className="h-8 w-8 border-slate-700 p-0 text-slate-300" disabled={busy === `${d.id}-plus`} onClick={() => adjust(d, { delta: 1 }, "plus")} data-testid={`del-plus-${d.id}`}><Plus className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-orange-300 hover:text-orange-200" onClick={() => { setCount(String(d.deliveries_today ?? "")); setEdit(d); }} data-testid={`del-set-${d.id}`}><Pencil className="h-4 w-4" /> Definir</Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-sky-300 hover:text-sky-200" onClick={() => { setHistForm({ date: today(), count: "" }); setHist(d); }} data-testid={`del-hist-${d.id}`}><History className="h-4 w-4" /> Histórico</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-sm border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Definir entregas de hoje</DialogTitle>
            <DialogDescription className="text-slate-400">Define o total de entregas do dia para {edit?.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-slate-300">Total de entregas hoje</Label>
            <Input type="number" min="0" value={count} onChange={(e) => setCount(e.target.value)} data-testid="del-count-input" className="border-slate-700 bg-slate-950 text-slate-100" />
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy === `${edit?.id}-set`} onClick={() => adjust(edit, { count: parseInt(count, 10) || 0 }, "set")} data-testid="del-count-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="max-w-sm border-slate-800 bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Corrigir histórico de entregas</DialogTitle>
            <DialogDescription className="text-slate-400">Defina o total de entregas de {hist?.name} numa data específica.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-300">Data</Label>
              <Input type="date" value={histForm.date} onChange={(e) => setHistForm((s) => ({ ...s, date: e.target.value }))} data-testid="del-hist-date" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Total de entregas na data</Label>
              <Input type="number" min="0" value={histForm.count} onChange={(e) => setHistForm((s) => ({ ...s, count: e.target.value }))} data-testid="del-hist-count" className="border-slate-700 bg-slate-950 text-slate-100" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-slate-400" onClick={() => setHist(null)}>Cancelar</Button>
            <Button className="bg-orange-500 text-white hover:bg-orange-600" disabled={busy === "hist"} onClick={saveHistory} data-testid="del-hist-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {historyDriver && (
        <DriverHistoryDialog driver={historyDriver} start={start} end={end} onClose={() => setHistoryDriver(null)} onSaved={onHistorySaved} />
      )}
    </div>
  );
}
